import { FormEvent, KeyboardEvent, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { OFFER_VERSION } from '../../../shared/criar-musica-catalog.mjs';
import { VOICE_GENDERS, buildCreateOrderPayload, onlyDigits, validEmail } from '../../../shared/criar-musica-form.mjs';
import { musicalStyles } from '../../mocks/home';
import { useDarkMode } from '../../hooks/useDarkMode';
import { apiFetch, saveAccessToken, saveLocalOrder } from './storage';
import { useOrder } from './useOrder';
import { Field } from './ui';
import './styles.css';

const TOTAL_STEPS = 4;
const DEFAULT_OCCASION = 'Homenagem';
const DEFAULT_MOOD = 'Emocionante';

const RELATIONSHIP_OPTIONS = [
  'Mãe',
  'Pai',
  'Filho(a)',
  'Namorado(a)',
  'Esposo(a)',
  'Amigo(a)',
  'Avô/Avó',
  'Irmão(ã)',
  'Para mim',
  'Outro',
];

const OCCASION_OPTIONS = [
  'Aniversário',
  'Dia das Mães',
  'Dia dos Pais',
  'Casamento',
  'Namoro',
  'Homenagem',
  'Formatura',
  'Natal',
  'Ano Novo',
];

const MOOD_OPTIONS = ['Emocionante', 'Alegre', 'Romântico', 'Saudade', 'Inspirador', 'Divertido'];

const STYLE_OPTIONS = [
  'Sertanejo',
  'Gospel',
  'Pagode',
  'MPB',
  'Pop',
  'Forró',
  'Funk',
  'Rock',
  'Rap',
  'Bossa Nova',
];

const EXAMPLE_QUALITIES = 'Ela é carinhosa, engraçada e teimosa do jeito dela. Sempre faz bolo de fubá no domingo e abraça apertado.';
const EXAMPLE_MEMORY = 'A viagem de fusca pra praia em 98, quando a gente se perdeu e riu a noite inteira.';
const EXAMPLE_HEART = 'Obrigado por nunca ter soltado a minha mão, mesmo nos dias difíceis.';

export type WizardForm = {
  recipientName: string;
  relationship: string;
  occasion: string;
  mood: string;
  qualities: string;
  memories: string;
  heartMessage: string;
  style: string;
  voiceGender: string;
  customerName: string;
  email: string;
  phone: string;
};

const initialForm: WizardForm = {
  recipientName: '',
  relationship: '',
  occasion: DEFAULT_OCCASION,
  mood: DEFAULT_MOOD,
  qualities: '',
  memories: '',
  heartMessage: '',
  style: '',
  voiceGender: '',
  customerName: '',
  email: '',
  phone: '',
};

export function canProceed(step: number, form: WizardForm) {
  if (step === 1) return Boolean(form.recipientName.trim() && form.relationship);
  if (step === 2) return form.qualities.trim().length >= 1;
  if (step === 3) return Boolean(form.style && form.voiceGender);
  if (step === 4) {
    const hasName = Boolean(form.customerName.trim());
    const hasEmail = validEmail(form.email);
    const hasWhatsapp = onlyDigits(form.phone).length >= 10;
    return hasName && (hasEmail || hasWhatsapp);
  }
  return false;
}

function missingReason(step: number, form: WizardForm): string {
  if (step === 1) {
    if (!form.recipientName.trim()) return 'Escreva o nome de quem vai receber a música.';
    if (!form.relationship) return 'Escolha quem essa pessoa é pra você.';
    return '';
  }
  if (step === 2) return form.qualities.trim().length >= 1 ? '' : 'Escreva uma frase sobre o que torna essa pessoa especial.';
  if (step === 3) {
    if (!form.style) return 'Escolha o estilo da música.';
    if (!form.voiceGender) return 'Escolha voz feminina ou masculina.';
    return '';
  }
  if (step === 4) {
    if (!form.customerName.trim()) return 'Escreva seu nome.';
    if (!validEmail(form.email) && onlyDigits(form.phone).length < 10) {
      return 'Deixe um WhatsApp ou um e-mail pra gente te avisar.';
    }
    return '';
  }
  return '';
}

export default function CriarMusicaWizardPage() {
  const navigate = useNavigate();
  const { setOrder } = useOrder('');
  const isDark = useDarkMode();
  const [step, setStep] = useState(1);
  const [form, setForm] = useState<WizardForm>(initialForm);
  const [showOptional, setShowOptional] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const progress = Math.round((step / TOTAL_STEPS) * 100);

  function update<K extends keyof WizardForm>(key: K, value: WizardForm[K]) {
    setForm((current) => ({ ...current, [key]: value }));
  }

  function goNext() {
    if (!canProceed(step, form) || loading) return;
    setError('');
    setStep((current) => Math.min(TOTAL_STEPS, current + 1));
  }

  function goBack() {
    if (loading) return;
    setError('');
    setStep((current) => Math.max(1, current - 1));
  }

  async function submit(event: FormEvent) {
    event.preventDefault();
    if (!canProceed(4, form) || loading) return;

    setLoading(true);
    setError('');

    try {
      const payload = buildCreateOrderPayload(form);
      const response = await fetch(apiFetch('create-order'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...payload, offer_version: OFFER_VERSION }),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(typeof data.error === 'string' ? data.error : 'create_order_failed');

      saveLocalOrder(data.order);
      if (data.order?.slug && data.order?.accessToken) saveAccessToken(data.order.slug, data.order.accessToken);
      setOrder(data.order);
      navigate(`/criar-musica/gravando/${data.order.slug}`);
    } catch (caught) {
      const message = caught instanceof Error ? caught.message : String(caught);
      setError(message.includes('contact_incomplete')
        ? 'Deixa um WhatsApp ou um e-mail pra gente te avisar quando a letra ficar pronta.'
        : 'Não consegui criar o pedido agora. Tenta de novo em instantes.');
    } finally {
      setLoading(false);
    }
  }

  const heading = useMemo(() => {
    if (step === 1) return 'Pra quem é essa música?';
    if (step === 2) return 'A história em poucas linhas';
    if (step === 3) return 'Como a música deve soar?';
    return 'Como te avisamos?';
  }, [step]);

  const description = useMemo(() => {
    if (step === 1) return 'Ouça 1 minuto da música, depois escolha o plano. Crie uma música personalizada em 4 passos.';
    if (step === 2) return 'Um recado curto já basta. Depois você ouve 1 minuto da música e só então escolhe o plano.';
    if (step === 3) return 'Escolha o ritmo e a voz. Você pode ouvir um exemplo antes de seguir.';
    return 'Seu nome, e um WhatsApp ou e-mail. Um dos dois já resolve.';
  }, [step]);

  const hint = canProceed(step, form) || loading ? '' : missingReason(step, form);

  return (
    <div className="cm-page">
      <header className="cm-header">
        <div className="cm-progress-row">
          <Link to="/" className="cm-logo">
            <img src={isDark ? '/logo-dark.png' : '/logo-light.png'} alt="Inspira Músicas" />
          </Link>
          <span className="cm-progress-label">Passo {step} de {TOTAL_STEPS}</span>
        </div>
        <div className="cm-progress" aria-hidden="true">
          <span style={{ width: `${progress}%` }} />
        </div>
      </header>

      <form className="cm-layout" onSubmit={submit}>
        <section className="cm-panel">
          <p className="cm-kicker">Criar música</p>
          <h1>{heading}</h1>
          <p className="cm-lead">{description}</p>

          {step === 1 ? (
            <>
              <Field label="Nome de quem vai receber" htmlFor="cm-recipient-name">
                <input
                  id="cm-recipient-name"
                  className="cm-input"
                  value={form.recipientName}
                  onChange={(event) => update('recipientName', event.target.value)}
                  maxLength={40}
                  autoComplete="off"
                  placeholder="Ex.: Maria"
                />
              </Field>
              <Field label="Pra quem é" group labelId="cm-relationship-label">
                <ChoiceGrid
                  ariaLabelledby="cm-relationship-label"
                  options={RELATIONSHIP_OPTIONS}
                  value={form.relationship}
                  onChange={(value) => update('relationship', value)}
                />
              </Field>
            </>
          ) : null}

          {step === 2 ? (
            <>
              <Field label="O que torna essa pessoa especial?" htmlFor="cm-qualities">
                <textarea
                  id="cm-qualities"
                  className="cm-textarea"
                  value={form.qualities}
                  onChange={(event) => update('qualities', event.target.value)}
                  rows={4}
                  placeholder="Ex.: ela sempre faz bolo de fubá no domingo"
                />
                {!form.qualities.trim() ? (
                  <button type="button" className="cm-example" onClick={() => update('qualities', EXAMPLE_QUALITIES)}>
                    Não sei o que escrever — usar um exemplo
                  </button>
                ) : null}
                <p className="cm-field-meta">Uma frase já basta. Você poderá completar depois.</p>
              </Field>
              <Field label="Ocasião" group labelId="cm-occasion-label">
                <ChoiceGrid
                  ariaLabelledby="cm-occasion-label"
                  options={OCCASION_OPTIONS}
                  value={form.occasion}
                  onChange={(value) => update('occasion', value)}
                />
              </Field>
              <Field label="Clima" group labelId="cm-mood-label">
                <ChoiceGrid
                  ariaLabelledby="cm-mood-label"
                  options={MOOD_OPTIONS}
                  value={form.mood}
                  onChange={(value) => update('mood', value)}
                />
              </Field>
              {showOptional ? (
                <>
                  <Field label="Uma memória (opcional)" htmlFor="cm-memories">
                    <textarea
                      id="cm-memories"
                      className="cm-textarea"
                      value={form.memories}
                      onChange={(event) => update('memories', event.target.value)}
                      rows={3}
                      placeholder="Ex.: a viagem de fusca pra praia em 98"
                    />
                    {!form.memories.trim() ? (
                      <button type="button" className="cm-example" onClick={() => update('memories', EXAMPLE_MEMORY)}>
                        Não sei o que escrever — usar um exemplo
                      </button>
                    ) : null}
                  </Field>
                  <Field label="Mensagem do coração (opcional)" htmlFor="cm-heart">
                    <textarea
                      id="cm-heart"
                      className="cm-textarea"
                      value={form.heartMessage}
                      onChange={(event) => update('heartMessage', event.target.value)}
                      rows={3}
                      placeholder="Ex.: obrigado por nunca soltar a minha mão"
                    />
                    {!form.heartMessage.trim() ? (
                      <button type="button" className="cm-example" onClick={() => update('heartMessage', EXAMPLE_HEART)}>
                        Não sei o que escrever — usar um exemplo
                      </button>
                    ) : null}
                  </Field>
                </>
              ) : (
                <button type="button" className="cm-ghost" onClick={() => setShowOptional(true)}>
                  Adicionar uma memória ou mensagem (opcional)
                </button>
              )}
            </>
          ) : null}

          {step === 3 ? (
            <>
              <Field label="Estilo" group labelId="cm-style-label">
                <ChoiceGrid
                  ariaLabelledby="cm-style-label"
                  options={STYLE_OPTIONS}
                  value={form.style}
                  onChange={(value) => update('style', value)}
                />
                {form.style ? <StyleSample style={form.style} /> : null}
              </Field>
              <Field label="Voz" group labelId="cm-voice-label">
                <ChoiceGrid
                  ariaLabelledby="cm-voice-label"
                  options={[...VOICE_GENDERS]}
                  value={form.voiceGender}
                  onChange={(value) => update('voiceGender', value)}
                />
              </Field>
            </>
          ) : null}

          {step === 4 ? (
            <>
              <Field label="Seu nome" htmlFor="cm-customer-name">
                <input
                  id="cm-customer-name"
                  className="cm-input"
                  value={form.customerName}
                  onChange={(event) => update('customerName', event.target.value)}
                  maxLength={120}
                  autoComplete="name"
                  placeholder="Ex.: Ana"
                />
              </Field>
              <p className="cm-note">Pode preencher só um dos dois — a gente te avisa por ali quando a letra ficar pronta.</p>
              <Field label="WhatsApp" htmlFor="cm-phone">
                <input
                  id="cm-phone"
                  className="cm-input"
                  type="tel"
                  inputMode="tel"
                  autoComplete="tel"
                  value={form.phone}
                  onChange={(event) => update('phone', event.target.value)}
                  placeholder="(11) 90000-0000"
                />
              </Field>
              <Field label="E-mail" htmlFor="cm-email">
                <input
                  id="cm-email"
                  className="cm-input"
                  type="email"
                  inputMode="email"
                  autoComplete="email"
                  value={form.email}
                  onChange={(event) => update('email', event.target.value)}
                  placeholder="voce@email.com"
                />
              </Field>
            </>
          ) : null}

          {error ? <p className="cm-error">{error}</p> : null}

          <div className="cm-nav">
            {hint ? <p className="cm-nav-hint" role="status">{hint}</p> : null}
            <div className="cm-nav-row">
              {step > 1 ? (
                <button type="button" className="cm-back" onClick={goBack} disabled={loading}>
                  Voltar
                </button>
              ) : (
                <span />
              )}
              {step < TOTAL_STEPS ? (
                <button
                  type="button"
                  className="cm-primary"
                  onClick={goNext}
                  disabled={!canProceed(step, form) || loading}
                >
                  Próximo
                </button>
              ) : (
                <button type="submit" className="cm-primary" disabled={!canProceed(4, form) || loading}>
                  {loading ? 'Criando…' : 'Criar pedido'}
                </button>
              )}
            </div>
          </div>
        </section>
      </form>
    </div>
  );
}

function ChoiceGrid({
  options,
  value,
  onChange,
  ariaLabelledby,
}: {
  options: string[];
  value: string;
  onChange: (value: string) => void;
  ariaLabelledby?: string;
}) {
  const refs = useRef<Array<HTMLButtonElement | null>>([]);
  const selectedIndex = options.indexOf(value);

  function handleKeyDown(event: KeyboardEvent, index: number) {
    let next = -1;
    if (event.key === 'ArrowRight' || event.key === 'ArrowDown') next = (index + 1) % options.length;
    if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') next = (index - 1 + options.length) % options.length;
    if (event.key === 'Home') next = 0;
    if (event.key === 'End') next = options.length - 1;
    if (next < 0) return;
    event.preventDefault();
    onChange(options[next]);
    refs.current[next]?.focus();
  }

  return (
    <div className="cm-choices" role="radiogroup" aria-labelledby={ariaLabelledby}>
      {options.map((option, index) => {
        const selected = value === option;
        return (
          <button
            key={option}
            type="button"
            ref={(element) => { refs.current[index] = element; }}
            role="radio"
            aria-checked={selected}
            tabIndex={selected || (selectedIndex < 0 && index === 0) ? 0 : -1}
            className={selected ? 'is-selected' : ''}
            onClick={() => onChange(option)}
            onKeyDown={(event) => handleKeyDown(event, index)}
          >
            <span className="cm-choice-check" aria-hidden="true">✓</span>
            {option}
          </button>
        );
      })}
    </div>
  );
}

function sampleUrlFor(style: string) {
  return musicalStyles.find((item) => item.label === style)?.audioUrl || '';
}

function StyleSample({ style }: { style: string }) {
  const url = sampleUrlFor(style);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const [playing, setPlaying] = useState(false);

  useEffect(() => {
    return () => {
      audioRef.current?.pause();
    };
  }, []);

  useEffect(() => {
    audioRef.current?.pause();
    setPlaying(false);
  }, [style]);

  if (!url) return null;

  async function toggle() {
    if (!audioRef.current) audioRef.current = new Audio(url);
    const audio = audioRef.current;
    if (audio.src !== url) audio.src = url;
    audio.preload = 'none';
    if (playing) {
      audio.pause();
      setPlaying(false);
      return;
    }
    try {
      await audio.play();
      setPlaying(true);
      audio.onended = () => setPlaying(false);
    } catch {
      setPlaying(false);
    }
  }

  return (
    <>
      <button
        type="button"
        className={`cm-sample${playing ? ' is-playing' : ''}`}
        onClick={() => { void toggle(); }}
        aria-pressed={playing}
      >
        {playing ? 'Parar amostra' : `Ouvir como soa o ${style}`}
      </button>
      <p className="cm-field-meta">É só um exemplo do ritmo — não é a sua música.</p>
    </>
  );
}
